import React from 'react';
import styles from './PriceIndicator.module.css';

interface PriceIndicatorProps {
  precio: number;
  precioMercadoMinimo?: number;
  precioMercadoPromedio?: number;
  precioMercadoMaximo?: number;
}

type PriceStatus = 'good' | 'fair' | 'high' | 'unavailable';

interface PriceStatusInfo {
  status: PriceStatus;
  label: string;
  color: string;
}

export const PriceIndicator: React.FC<PriceIndicatorProps> = ({
  precio,
  precioMercadoMinimo,
  precioMercadoPromedio,
  precioMercadoMaximo
}) => {
  
  const getPriceStatus = (): PriceStatusInfo => {
    // If market data is not available, show "No disponible" with gray
    if (!precioMercadoPromedio || precioMercadoPromedio === 0 || !precioMercadoMaximo || precioMercadoMaximo === 0) {
      return {
        status: 'unavailable',
        label: 'No disponible',
        color: '#6b7280' // Gray
      };
    }
    
    // Logic based on user requirements:
    // precio <= promedio: "Buen Precio" with green checkmark
    if (precio <= precioMercadoPromedio) {
      return {
        status: 'good',
        label: 'Buen Precio',
        color: '#22c55e' // Green
      };
    }
    
    // precio > promedio but <= maximo: "Precio Justo" with orange checkmark
    if (precio > precioMercadoPromedio && precio <= precioMercadoMaximo) {
      return {
        status: 'fair',
        label: 'Precio Justo',
        color: '#ffa500' // Orange
      };
    }
    
    // precio > maximo: "Revisa Precio" with orange exclamation mark
    return {
      status: 'high',
      label: 'Revisa Precio',
      color: '#ffa500' // Orange
    };
  };

  const priceInfo = getPriceStatus();

  const PriceIcon: React.FC<{ status: PriceStatus; color: string }> = ({ status, color }) => {
    if (status === 'unavailable') {
      // Horizontal dash icon for "No disponible"
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" fill={color} />
          <line
            x1="8"
            y1="12"
            x2="16"
            y2="12"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      );
    } else if (status === 'good' || status === 'fair') {
      // Checkmark icon for "Buen Precio" and "Precio Justo"
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" fill={color} />
          <path
            d="M9 12l2 2 4-4"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    } else {
      // Exclamation mark for "Revisa Precio"
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" fill={color} />
          <line
            x1="12"
            y1="8"
            x2="12"
            y2="12"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <circle cx="12" cy="16" r="1" fill="white" />
        </svg>
      );
    }
  };

  return (
    <div className={styles.priceIndicator}>
      <div className={styles.priceIconContainer}>
        <PriceIcon status={priceInfo.status} color={priceInfo.color} />
      </div>
      <span className={styles.priceLabel} style={{ color: '#fff' }}>
        {priceInfo.label}
      </span>
    </div>
  );
};